import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Check, Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { Input } from './ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DRAFT_KEY = (orgId) => `maturity-draft-${orgId || 'unscoped'}`;

/**
 * MaturityAssessment — 30-question wizard, one pillar per step.
 * Props:
 *   - organizationId / organizationName (optional, persisted with the assessment)
 *   - onComplete({ assessment }) — called on successful submit
 *   - onCancel — called when user dismisses
 */
export default function MaturityAssessment({
  organizationId, organizationName, industry, onComplete, onCancel,
}) {
  const [pillars, setPillars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(0);  // -1 = intro, 0..4 = pillars, 5 = review/contact
  const [answers, setAnswers] = useState({});
  const [assessorName, setAssessorName] = useState('');
  const [assessorEmail, setAssessorEmail] = useState('');

  // Load taxonomy + draft
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/maturity/pillars`, { withCredentials: true });
        if (!alive) return;
        setPillars(r.data?.pillars || []);
        // Restore draft
        try {
          const draft = JSON.parse(localStorage.getItem(DRAFT_KEY(organizationId)) || 'null');
          if (draft && typeof draft.answers === 'object') {
            setAnswers(draft.answers);
            if (draft.assessorName) setAssessorName(draft.assessorName);
            if (draft.assessorEmail) setAssessorEmail(draft.assessorEmail);
          }
        } catch (err) {
          console.warn('maturity draft restore failed', err);
        }
      } catch (err) {
        console.error('maturity pillars load failed', err);
        toast.error('Could not load the maturity questionnaire.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [organizationId]);

  // Persist draft on every answer change
  useEffect(() => {
    if (loading) return;
    try {
      localStorage.setItem(DRAFT_KEY(organizationId), JSON.stringify({
        answers, assessorName, assessorEmail,
      }));
    } catch (err) {
      console.warn('maturity draft save failed', err);
    }
  }, [answers, assessorName, assessorEmail, organizationId, loading]);

  const current = pillars[step];
  const totalSteps = pillars.length;

  const currentAnsweredCount = useMemo(() => {
    if (!current) return 0;
    return current.dimensions.filter((d) => answers[d.id] != null).length;
  }, [current, answers]);

  const allAnsweredCount = useMemo(() => {
    let n = 0;
    pillars.forEach((p) => p.dimensions.forEach((d) => { if (answers[d.id] != null) n += 1; }));
    return n;
  }, [pillars, answers]);

  const totalQuestions = useMemo(
    () => pillars.reduce((acc, p) => acc + p.dimensions.length, 0),
    [pillars]
  );

  const canAdvance = current && currentAnsweredCount === current.dimensions.length;
  const isReviewStep = step >= totalSteps;
  const canSubmit = !submitting && allAnsweredCount === totalQuestions;

  const handlePick = (dimId, level) => {
    setAnswers((prev) => ({ ...prev, [dimId]: Number(level) }));
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/maturity/submit`,
        {
          answers,
          organization_id: organizationId || null,
          organization_name: organizationName || null,
          industry: industry || null,
          assessor_name: assessorName || null,
          assessor_email: assessorEmail || null,
        },
        { withCredentials: true }
      );
      toast.success(`Assessment saved · score ${Math.round(r.data?.overall_score || 0)}/100 (${r.data?.maturity_band})`);
      try { localStorage.removeItem(DRAFT_KEY(organizationId)); } catch (err) {
        console.warn('maturity draft clear failed', err);
      }
      onComplete?.({ assessment: r.data });
    } catch (err) {
      console.error('maturity submit failed', err);
      toast.error(err?.response?.data?.detail || 'Could not save the assessment.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="maturity-wizard-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!pillars.length) {
    return (
      <div className="py-12 text-center text-slate-500" data-testid="maturity-wizard-empty">
        <AlertCircle className="w-8 h-8 mx-auto mb-2 text-rose-500" />
        Questionnaire could not be loaded.
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="maturity-wizard">
      {/* Progress bar */}
      <div>
        <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
          <span>
            {isReviewStep ? 'Review & submit' : `Pillar ${step + 1} of ${totalSteps}`}
          </span>
          <span>{allAnsweredCount} / {totalQuestions} answered</span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#0F2040] transition-all duration-300"
            style={{ width: `${(allAnsweredCount / totalQuestions) * 100}%` }}
            data-testid="maturity-wizard-progress"
          />
        </div>
      </div>

      {!isReviewStep && current && (
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <div className="text-xs uppercase tracking-wider text-cyan-700 font-semibold">
              Pillar {step + 1}
            </div>
            <CardTitle className="text-xl text-[#0F2040]">{current.name}</CardTitle>
            {current.description ? (
              <p className="text-sm text-slate-500">{current.description}</p>
            ) : null}
          </CardHeader>
          <CardContent className="space-y-6">
            {current.dimensions.map((d, di) => {
              const picked = answers[d.id];
              return (
                <div key={d.id} className="border-t border-slate-100 first:border-t-0 pt-4 first:pt-0">
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-xs font-mono text-slate-400">{di + 1}.{step + 1}</span>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-slate-800">{d.name}</div>
                      <div className="text-sm text-slate-600">{d.question}</div>
                    </div>
                  </div>
                  <RadioGroup
                    value={picked != null ? String(picked) : ''}
                    onValueChange={(v) => handlePick(d.id, v)}
                    className="space-y-1.5 mt-2"
                    data-testid={`maturity-dim-${d.id}`}
                  >
                    {d.options.map((opt) => {
                      const id = `${d.id}-l${opt.level}`;
                      const isPicked = picked === opt.level;
                      return (
                        <div
                          key={id}
                          className={`flex items-start gap-3 rounded border px-3 py-2 cursor-pointer transition ${
                            isPicked ? 'border-[#4ABFCF] bg-cyan-50/40' : 'border-slate-200 hover:bg-slate-50'
                          }`}
                          onClick={() => handlePick(d.id, opt.level)}
                          data-testid={`maturity-opt-${d.id}-${opt.level}`}
                        >
                          <RadioGroupItem value={String(opt.level)} id={id} className="mt-1" />
                          <Label htmlFor={id} className="flex-1 cursor-pointer text-sm text-slate-700">
                            <span className="text-xs font-semibold text-slate-400 mr-2">L{opt.level}</span>
                            {opt.text}
                          </Label>
                        </div>
                      );
                    })}
                  </RadioGroup>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {isReviewStep && (
        <Card className="border-slate-200" data-testid="maturity-review">
          <CardHeader>
            <CardTitle className="text-xl text-[#0F2040]">Ready to submit</CardTitle>
            <p className="text-sm text-slate-500">
              You've answered {allAnsweredCount} of {totalQuestions} questions across {totalSteps} pillars.
              Add your contact details below (optional) and we'll save the assessment to{' '}
              <span className="font-semibold">{organizationName || 'this customer org'}</span>.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Assessor name</Label>
                <Input
                  value={assessorName}
                  onChange={(e) => setAssessorName(e.target.value)}
                  placeholder="Jane Doe"
                  data-testid="maturity-assessor-name"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Assessor email</Label>
                <Input
                  type="email"
                  value={assessorEmail}
                  onChange={(e) => setAssessorEmail(e.target.value)}
                  placeholder="jane@customer.com"
                  data-testid="maturity-assessor-email"
                />
              </div>
            </div>
            <div className="space-y-1.5 text-xs text-slate-500">
              {pillars.map((p) => {
                const answered = p.dimensions.filter((d) => answers[d.id] != null).length;
                return (
                  <div key={p.id} className="flex items-center justify-between">
                    <span>{p.name}</span>
                    <span className={answered === p.dimensions.length ? 'text-emerald-600 font-medium' : 'text-rose-600'}>
                      {answered}/{p.dimensions.length} {answered === p.dimensions.length ? '✓' : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between border-t border-slate-200 pt-4">
        <Button
          variant="outline"
          onClick={() => (step === 0 ? onCancel?.() : setStep((s) => Math.max(0, s - 1)))}
          disabled={submitting}
          data-testid="maturity-wizard-back"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          {step === 0 ? 'Cancel' : 'Back'}
        </Button>

        {!isReviewStep ? (
          <Button
            onClick={() => setStep((s) => s + 1)}
            disabled={!canAdvance || submitting}
            data-testid="maturity-wizard-next"
            className="bg-[#0F2040] hover:bg-[#1a2f5a]"
          >
            {step === totalSteps - 1 ? 'Review' : 'Next'}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="maturity-wizard-submit"
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Check className="w-4 h-4 mr-1" />
            )}
            {submitting ? 'Saving…' : 'Submit assessment'}
          </Button>
        )}
      </div>
    </div>
  );
}
